import { ReactElement, forwardRef } from 'react';

import { cn } from '../../utils/cn';
import { Text } from '../Text';

// Only allow string or SVG icon as children
interface BadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  rounded?: 'full' | 'none' | 'xs' | 'sm';
  /** Badge color scheme */
  color?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'error'
    | 'warning'
    | 'special'
    | 'plain';
  /** Badge size variant */
  size?: 'xs' | 'sm' | 'md';
  /** Left decorator icon */
  leftDecorator?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /** Right decorator icon */
  rightDecorator?: React.ComponentType<React.SVGProps<SVGSVGElement>>;

  /** Badge content */
  children?: string | ReactElement<SVGSVGElement>;
}

const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    {
      color = 'secondary',
      size = 'md',
      rounded = 'full',
      leftDecorator: LeftIcon,
      rightDecorator: RightIcon,
      className,
      children,
      ...props
    },
    ref
  ) => {
    const isString = typeof children === 'string';
    const baseStyles = cn(
      'inline-flex items-center justify-center',

      {
        'rounded-xs': rounded === 'xs',
        'rounded-sm': rounded === 'sm',
        'rounded-full': rounded === 'full',
        'rounded-none': rounded === 'none',
        border: rounded === 'full' || color === 'plain',
        'gap-0.5 px-1.5 py-0.5': size !== 'md',
        'gap-1 px-2 py-1': size === 'md',
      }
    );

    const getColorStyles = () => {
      switch (color) {
        case 'primary':
          return 'bg-brand-tertiary text-brand-primary border-[var(--brand-200)]';
        case 'success':
          return 'bg-success-secondary text-success border-success';
        case 'error':
          return 'bg-error-secondary text-error border-error';
        case 'warning':
          return 'bg-warning-secondary text-warning border-warning';
        case 'special':
          return 'bg-purple text-purple border-purple';
        case 'plain':
          return 'bg-primary text-primary border-secondary';
        case 'secondary':
        default:
          return 'bg-quaternary text-secondary border-secondary';
      }
    };

    return (
      <div className={cn(baseStyles, getColorStyles(), className)} {...props}>
        {LeftIcon && <LeftIcon className={cn('size-3 flex-shrink-0')} />}
        {isString ? (
          <Text
            ref={ref}
            as="span"
            variant={size === 'xs' ? 'xs' : 'sm'}
            weight="medium"
          >
            {children}
          </Text>
        ) : (
          children
        )}
        {RightIcon && <RightIcon className={cn('size-3 flex-shrink-0')} />}
      </div>
    );
  }
);

Badge.displayName = 'Badge';

export { Badge };
export type { BadgeProps };
